import {AfterViewInit, Component, ElementRef, OnDestroy, ViewChild} from '@angular/core';
import {Router} from '@angular/router';
import {AutorizacaoService} from '../../services/autorizacao.service';
import {ComandaService, ResultadoValidacaoComanda} from '../../services/comanda.service';

@Component({
  selector: 'app-validar-comanda',
  templateUrl: './validar-comanda.component.html',
  styleUrls: ['./validar-comanda.component.scss']
})
export class ValidarComandaComponent implements AfterViewInit, OnDestroy {
  @ViewChild('campoComanda') campoComanda: ElementRef<HTMLInputElement>;
  @ViewChild('historicoSection') historicoSection: ElementRef<HTMLElement>;

  codigoComanda = '';
  cameraAtiva = false;
  validando = false;
  somAtivo = true;
  mostrarTodas = false;
  erro = '';
  resultado: ResultadoValidacaoComanda | null = null;
  historico: ResultadoValidacaoComanda[] = [];

  private audioContext: AudioContext | null = null;
  private destruido = false;
  private readonly storageKey: string;

  constructor(private comandaService: ComandaService,
              private autorizacaoService: AutorizacaoService,
              private router: Router) {
    const idEmpresa = this.autorizacaoService.getUser()?.empresa?.id || 'empresa';
    this.storageKey = `validacoes-comanda:${idEmpresa}`;
    this.historico = this.leiaHistorico();
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.campoComanda?.nativeElement?.focus());
  }

  ngOnDestroy(): void {
    this.destruido = true;
    this.cameraAtiva = false;
    if(this.audioContext) {
      this.audioContext.close().catch(() => undefined);
      this.audioContext = null;
    }
  }

  get historicoVisivel(): ResultadoValidacaoComanda[] {
    return this.mostrarTodas ? this.historico : this.historico.slice(0, 3);
  }

  get tituloErro(): string {
    const mensagem = this.erro.toLocaleLowerCase('pt-BR');
    if(mensagem.includes('não está vinculado') || mensagem.includes('não possui uma comanda'))
      return 'Comanda não encontrada';
    if(mensagem.includes('não encontramos o cartão') || mensagem.includes('cartão') && mensagem.includes('não encontrado'))
      return 'Cartão não existe';
    if(mensagem.includes('permissão')) return 'Permissão da câmera necessária';
    if(mensagem.includes('câmera') || mensagem.includes('camera')) return 'Câmera indisponível';
    if(mensagem.includes('qr code')) return 'QR Code inválido';
    if(mensagem.includes('digite') || mensagem.includes('informe')) return 'Informe o cartão';
    return 'Não foi possível validar';
  }

  get categoriaErro(): string {
    const mensagem = this.erro.toLocaleLowerCase('pt-BR');
    if(mensagem.includes('não está vinculado') || mensagem.includes('não possui uma comanda')) return 'comanda-ausente';
    if(mensagem.includes('não encontramos o cartão') || mensagem.includes('cartão') && mensagem.includes('não encontrado'))
      return 'cartao-ausente';
    if(mensagem.includes('permissão') || mensagem.includes('câmera') || mensagem.includes('camera')) return 'camera';
    if(mensagem.includes('qr code') || mensagem.includes('digite') || mensagem.includes('informe')) return 'entrada';
    return 'falha';
  }

  get rotuloErro(): string {
    switch(this.categoriaErro) {
      case 'comanda-ausente': return 'Sem comanda vinculada';
      case 'cartao-ausente': return 'Cartão não localizado';
      case 'camera': return 'Leitor indisponível';
      case 'entrada': return 'Confira os dados';
      default: return 'Falha na validação';
    }
  }

  get iconeErro(): string {
    switch(this.categoriaErro) {
      case 'comanda-ausente': return 'fa-link';
      case 'cartao-ausente': return 'fa-credit-card';
      case 'camera': return 'fa-camera';
      case 'entrada': return this.erro.toLocaleLowerCase('pt-BR').includes('qr code') ? 'fa-qrcode' : 'fa-keyboard';
      default: return 'fa-exclamation';
    }
  }

  voltar(): void {
    this.router.navigate(['/admin/pedidos-mesas']);
  }

  irParaInicio(): void {
    this.router.navigate(['/admin/index']);
  }

  exibirHistorico(): void {
    this.fecharCamera();
    setTimeout(() => this.historicoSection?.nativeElement?.scrollIntoView({behavior: 'smooth', block: 'start'}));
  }

  focarCampo(): void {
    this.fecharCamera();
    setTimeout(() => this.campoComanda?.nativeElement?.focus());
  }

  async iniciarLeitor(): Promise<void> {
    this.erro = '';
    this.resultado = null;
    await this.prepareAudio();
    this.cameraAtiva = true;
  }

  fecharCamera(): void {
    this.cameraAtiva = false;
    this.resultado = null;
    this.erro = '';
    setTimeout(() => this.campoComanda?.nativeElement?.focus());
  }

  async validarDigitado(): Promise<void> {
    const codigo = String(this.codigoComanda || '').trim();
    if(!codigo) {
      this.erro = 'Digite o número da comanda.';
      this.campoComanda?.nativeElement?.focus();
      return;
    }

    await this.prepareAudio();
    await this.validar(codigo);
  }

  async aoLerCodigo(codigo: string): Promise<void> {
    if(!codigo || this.validando || this.resultado) return;
    await this.validar(String(codigo).trim());
  }

  aoResponderPermissaoCamera(permitida: boolean): void {
    if(permitida !== false) return;
    this.cameraAtiva = false;
    this.erro = 'Não foi possível acessar a câmera. Libere a permissão no navegador e tente novamente.';
  }

  aoFalharCamera(): void {
    this.cameraAtiva = false;
    this.erro = 'A câmera não pôde ser iniciada neste aparelho.';
  }

  continuarLendo(): void {
    this.resultado = null;
    this.erro = '';
    this.cameraAtiva = false;
    setTimeout(() => {
      if(!this.destruido) this.cameraAtiva = true;
    });
  }

  fecharErro(): void {
    if(this.cameraAtiva) {
      this.continuarLendo();
      return;
    }
    this.novaValidacao();
  }

  novaValidacao(): void {
    this.resultado = null;
    this.erro = '';
    this.codigoComanda = '';
    setTimeout(() => this.campoComanda?.nativeElement?.focus());
  }

  async alternarSom(): Promise<void> {
    this.somAtivo = !this.somAtivo;
    if(this.somAtivo) await this.prepareAudio();
  }

  identificacaoComanda(item: ResultadoValidacaoComanda): string {
    return item.codigo || String(item.id);
  }

  identificacaoMesa(item: ResultadoValidacaoComanda): string {
    return item.mesa?.nome ? `Mesa ${item.mesa.nome}` : 'Sem mesa';
  }

  private async validar(codigo: string): Promise<void> {
    this.validando = true;
    this.erro = '';
    this.resultado = null;
    try {
      const resultado = await this.comandaService.validePagamento(codigo);
      if(this.destruido) return;

      this.resultado = resultado;
      this.codigoComanda = '';
      this.registreNoHistorico(resultado);
      this.toqueResultado(resultado.paga);
    } catch (erro: any) {
      if(this.destruido) return;
      this.erro = typeof erro === 'string' ? erro : (erro?.message || 'Não foi possível validar a comanda.');
    } finally {
      this.validando = false;
    }
  }

  private registreNoHistorico(resultado: ResultadoValidacaoComanda): void {
    this.historico = [resultado, ...this.historico.filter((item) => item.id !== resultado.id)].slice(0, 30);
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.historico));
    } catch (erro) {
      console.warn('Não foi possível salvar o histórico de validações da comanda.', erro);
    }
  }

  private leiaHistorico(): ResultadoValidacaoComanda[] {
    try {
      const salvo = JSON.parse(localStorage.getItem(this.storageKey) || '[]');
      return Array.isArray(salvo) ? salvo : [];
    } catch (erro) {
      return [];
    }
  }

  private async prepareAudio(): Promise<void> {
    if(!this.somAtivo) return;
    const AudioContextConstructor = window.AudioContext || (window as any).webkitAudioContext;
    if(!AudioContextConstructor) return;

    try {
      if(!this.audioContext) this.audioContext = new AudioContextConstructor();
      if(this.audioContext.state === 'suspended') await this.audioContext.resume();
    } catch (erro) {
      console.warn('Não foi possível preparar o som da validação da comanda.', erro);
    }
  }

  private toqueResultado(paga: boolean): void {
    if(!this.somAtivo || !this.audioContext) return;

    const audioContext = this.audioContext;
    const inicio = audioContext.currentTime;
    const notas = paga ? [
      {frequencia: 740, inicio: 0, duracao: 0.12},
      {frequencia: 988, inicio: 0.14, duracao: 0.2}
    ] : [
      {frequencia: 330, inicio: 0, duracao: 0.18},
      {frequencia: 220, inicio: 0.2, duracao: 0.28}
    ];

    notas.forEach((nota) => {
      const oscilador = audioContext.createOscillator();
      const ganho = audioContext.createGain();
      oscilador.type = paga ? 'sine' : 'triangle';
      oscilador.frequency.setValueAtTime(nota.frequencia, inicio + nota.inicio);
      ganho.gain.setValueAtTime(0.0001, inicio + nota.inicio);
      ganho.gain.exponentialRampToValueAtTime(0.24, inicio + nota.inicio + 0.015);
      ganho.gain.exponentialRampToValueAtTime(0.0001, inicio + nota.inicio + nota.duracao);
      oscilador.connect(ganho);
      ganho.connect(audioContext.destination);
      oscilador.start(inicio + nota.inicio);
      oscilador.stop(inicio + nota.inicio + nota.duracao);
    });
  }
}
